"use client";

import { useGetSubscriptionStatus } from "@/app/api/__generated__/endpoints/credits/credits";
import { useCopilotUsage } from "@/app/(platform)/copilot/components/UsageLimits/useCopilotUsage";
import { useTrialStatus } from "@/services/trials/useTrialStatus";
import { usePlatformPayment } from "@/hooks/usePlatformPayment";
import { getUsageBudget } from "./budget";
import { getUsageExperience } from "./helpers";

export type UsageSnapshot = Pick<
  ReturnType<typeof useUsageExperience>,
  "budget" | "usage" | "experience"
>;

export function useUsageExperience() {
  const usage = useCopilotUsage();
  const trial = useTrialStatus();
  const subscription = useGetSubscriptionStatus({
    query: {
      select: (response) =>
        response.status === 200 ? response.data : undefined,
    },
  });
  const { isPaymentEnabled: isBillingEnabled } = usePlatformPayment();
  const experience = getUsageExperience(
    usage.data,
    trial.data,
    subscription.data?.tier,
  );
  const trialUnavailable =
    experience.tier === "TRIAL" && (trial.isError || !trial.data);
  return {
    experience,
    budget: getUsageBudget(usage.data),
    usage: usage.data,
    trial: trial.data,
    subscription: subscription.data,
    isBillingEnabled,
    isLoading:
      usage.isLoading || (experience.tier === "TRIAL" && trial.isLoading),
    isError:
      usage.isError || (!usage.isLoading && !usage.data) || trialUnavailable,
    retry: (throwOnError = false) =>
      Promise.all([
        usage.refetch({ throwOnError }),
        trial.refetch({ throwOnError }),
        subscription.refetch({ throwOnError }),
      ]),
  };
}
